// A record module (work reports, measurements, site documentation) made usable offline. The
// module's api.js keeps its function names and answer shapes; behind them:
// - get/list read through the cache and show the outbox on top: records created offline (local
//   ids "local-…"), queued edits and photos, each marked with `offline` { pending, state, error }
// - create/update/addPhoto go out at once when possible, otherwise into the outbox
// - actions that need the server (sign, e-mail, PDF …) on a record that is not synced yet fail
//   with a clear message instead of a network error
import i18next from "i18next";
import axios from "axios";
import {
  STATES,
  cachedRead,
  discardItem,
  itemsOf,
  loadPendingPhoto,
  mutate,
  newLocalId,
  outbox,
  pendingPhotoUrl,
  readCache,
  isLocalId,
  writeCache
} from "./index";

const pathGet = (object, path) =>
  String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((value, key) => (value == null ? undefined : value[key]), object);

const SEVERITY = [STATES.CONFLICT, STATES.ERROR, STATES.AUTH, STATES.PENDING];

/** { pending, state, error } of a record's queued items (the worst state wins). */
export const offlineMarker = items => {
  if (!items || !items.length) return null;
  const worst = items
    .slice()
    .sort((a, b) => SEVERITY.indexOf(a.state) - SEVERITY.indexOf(b.state))[0];
  return {
    pending: items.length,
    state: worst.state,
    error: worst.error || null,
    itemId: worst.id
  };
};

// Shaped like an axios error, so the modules' error alerts show the text
const requestError = (status, message) => {
  const error = new Error(message);
  error.response = { status, data: { errorMessage: message, message } };
  return error;
};

export const notSyncedError = () =>
  requestError(
    409,
    i18next.t("Not synced yet. This is possible as soon as the record has been uploaded.", {
      ns: "dashboard"
    })
  );

/**
 * module      outbox/cache name, e.g. "work-reports"
 * base        API base URL of the module
 * auth        () => axios config with the Authorization header
 * idPath, versionPath   where a create/update answer carries the id and updatedAt
 * unwrap/wrap server answer ↔ record view ({ data: view } or the view itself)
 * blank       async (body, localId) => view of a record created offline
 * toRow       (view) => list row of such a record (default: the view)
 * label       (body) => text for the pending-changes list
 */
export function createResource({
  module,
  base,
  auth,
  idPath,
  versionPath,
  unwrap,
  wrap,
  blank,
  toRow = view => view,
  label = () => "",
  photoField = "photos"
}) {
  const recordKey = id => `${module}:${id}`;

  const overlay = async (view, items) => {
    let result = { ...view };
    for (const item of items) {
      if (item.kind === "update") result = { ...result, ...item.body };
      if (item.kind === "photo") {
        await loadPendingPhoto(item);
        result[photoField] = [
          ...(result[photoField] || []),
          {
            _id: item.photoId,
            caption: (item.body && item.body.caption) || "",
            width: Number(item.body && item.body.width) || 0,
            height: Number(item.body && item.body.height) || 0,
            pending: true
          }
        ];
      }
    }
    const marker = offlineMarker(items);
    return marker ? { ...result, offline: marker } : result;
  };

  /** The record view: server (or its cached copy) or the local create, with the queue applied. */
  const view = async (id, { network = true } = {}) => {
    const resolved = await outbox.resolveId(id);
    const items = await itemsOf(module, resolved, id);
    let current;
    if (isLocalId(resolved)) {
      const create = items.find(item => item.kind === "create");
      if (!create) {
        throw requestError(404, i18next.t("This change was discarded.", { ns: "dashboard" }));
      }
      current = await blank(create.body, resolved);
    } else if (network) {
      const response = await cachedRead(recordKey(resolved), () =>
        axios.get(`${base}/${resolved}`, auth())
      );
      current = unwrap(response.data);
      if (response.fromCache) current = { ...current, offlineCopyAt: response.cachedAt };
    } else {
      const hit = await readCache(recordKey(resolved));
      if (!hit) throw requestError(404, "Not available offline");
      current = unwrap(hit.data);
    }
    // A create's body is already the local view; it still counts as pending
    return overlay(current, items);
  };

  const queuedAnswer = async id => ({
    data: wrap(await view(id, { network: false })),
    status: 202,
    headers: {},
    queued: true
  });

  const get = async id => ({ data: wrap(await view(id)), status: 200, headers: {} });

  /** List answer ({ data: rows, … }) with records created offline first and queued rows marked. */
  const list = async (key, request, { includeLocal = true } = {}) => {
    const response = await cachedRead(`${module}:list:${key}`, request);
    const items = (await outbox.list()).filter(item => item.module === module);
    if (!items.length) return response;
    const byEntity = new Map();
    items.forEach(item => byEntity.set(item.entity, [...(byEntity.get(item.entity) || []), item]));
    const rows = (response.data?.data || []).map(row => {
      const marker = offlineMarker(byEntity.get(`${module}:${row._id}`));
      return marker ? { ...row, offline: marker } : row;
    });
    const local = includeLocal
      ? await Promise.all(
          items
            .filter(item => item.kind === "create")
            .map(async item =>
              toRow(await overlay(await blank(item.body, item.localId), byEntity.get(item.entity)))
            )
        )
      : [];
    return { ...response, data: { ...response.data, data: [...local, ...rows] } };
  };

  const create = async body => {
    const localId = newLocalId();
    const result = await mutate({
      module,
      kind: "create",
      method: "POST",
      url: base,
      localId,
      body,
      idPath,
      versionPath,
      label: label(body)
    });
    if (!result.queued) {
      writeCache(recordKey(pathGet(result.response.data, idPath)), result.response.data);
      return result.response;
    }
    return queuedAnswer(localId);
  };

  const update = async (id, body) => {
    const resolved = await outbox.resolveId(id);
    // The version the user edited: a replay is refused if someone changed it meanwhile
    const known = isLocalId(resolved) ? null : await readCache(recordKey(resolved));
    const result = await mutate({
      module,
      kind: "update",
      method: "PUT",
      url: `${base}/{id}`,
      target: resolved,
      body,
      versionPath,
      baseVersion: known ? pathGet(known.data, versionPath) : undefined,
      label: label(body)
    });
    if (!result.queued) {
      writeCache(recordKey(resolved), result.response.data);
      return result.response;
    }
    return queuedAnswer(resolved);
  };

  /** fields: the multipart text fields of the module's photo upload */
  const addPhoto = async (id, blob, fields, name) => {
    const resolved = await outbox.resolveId(id);
    const result = await mutate({
      module,
      kind: "photo",
      method: "POST",
      url: `${base}/{id}/photos`,
      target: resolved,
      body: fields,
      files: [{ field: "photo", blob, name }],
      versionPath,
      photoId: newLocalId(),
      label: i18next.t("Photo", { ns: "dashboard" })
    });
    if (!result.queued) {
      writeCache(recordKey(resolved), result.response.data);
      return result.response;
    }
    return queuedAnswer(resolved);
  };

  /** A photo still on this device is dropped from the queue; others need the server. */
  const removePhoto = async (id, photoId, request) => {
    if (isLocalId(photoId)) {
      const item = (await outbox.list()).find(other => other.photoId === photoId);
      if (item) await discardItem(item.id);
      return { data: wrap(await view(id)), status: 200, headers: {} };
    }
    return whenSynced(id, request);
  };

  /** A record created offline and not synced yet is simply dropped from the queue. */
  const remove = async (id, request) => {
    const resolved = await outbox.resolveId(id);
    if (isLocalId(resolved)) {
      const create = (await itemsOf(module, resolved)).find(item => item.kind === "create");
      if (create) await discardItem(create.id);
      return { data: {}, status: 200, headers: {} };
    }
    return request(resolved);
  };

  /** Server-only actions: run with the server id, or explain why not yet. */
  const whenSynced = async (id, request) => {
    const resolved = await outbox.resolveId(id);
    if (isLocalId(resolved)) throw notSyncedError();
    return request(resolved);
  };

  const photoUrl = (photoId, serverUrl) =>
    isLocalId(photoId) ? pendingPhotoUrl(photoId) : serverUrl();

  return { get, view, list, create, update, addPhoto, removePhoto, remove, whenSynced, photoUrl };
}
